/**
 * OrderImagesCells Component  
 * Renders the three image-related columns (Image, Image Type, View) for order tables.
 * Shows OrderImage records (S3) and/or metadata.woo_drawings (external URLs from WooCommerce).
 */

import React, { useState } from 'react';
import { Button } from './Button';
import { useListOrderImagesQuery } from '../../store/slices/apiSlice';
import { OrderImagesModal } from './OrderImagesModal';
import { WooDrawingsModal } from './WooDrawingsModal';
import type { OrderImage } from '../../utils/types';

interface OrderImagesCellsProps {
  orderId: string;
  /** Optional order object for metadata.woo_drawings (WooCommerce uploads) */
  order?: { metadata?: { woo_drawings?: Array<{ type_code?: number; label?: string; url?: string }> } } | null;
}

export const OrderImagesCells: React.FC<OrderImagesCellsProps> = ({ orderId, order }) => {
  const id = String(orderId ?? '').trim();
  const { data, isFetching, isError } = useListOrderImagesQuery(
    { orderId: id },
    { skip: !id, refetchOnMountOrArgChange: true }
  );
  const [openS3, setOpenS3] = useState(false);
  const [openWoo, setOpenWoo] = useState(false);

  const images: OrderImage[] = data?.results ?? [];
  // API queryset is already active-only; treat missing is_active as true (avoid dropping rows if field omitted).
  const active = images.filter((i) => {
    const row = i as Record<string, unknown>;
    if (row.deleted_at != null && row.deleted_at !== '') return false;
    return row.is_active !== false;
  });
  const rawWoo = (order?.metadata as Record<string, unknown> | undefined)?.woo_drawings;
  const wooDrawings: Array<{ type_code?: number; label?: string; url?: string }> = Array.isArray(rawWoo)
    ? rawWoo
    : rawWoo && typeof rawWoo === 'object'
      ? [rawWoo as { type_code?: number; label?: string; url?: string }]
      : [];
  const wooList = wooDrawings.filter((d) => d?.url);
  const hasS3 = active.length > 0;
  const hasWoo = wooList.length > 0;
  const has = hasS3 || hasWoo;
  
  const cap = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  const featureLabel = (f: string) => ({
    drilled_holes: 'Drilled Holes',
    rounded_corners: 'Rounded Corners',
    decorative_leads: 'Decorative Leads',
    duplex_bar: 'Duplex Bar',
    georgian_bar: 'Georgian Bar',
    cutout: 'Cutout',
    quote_drawing: 'Quote Drawing',
  } as Record<string, string>)[f] || f.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  
  const pairs = active.map(i => `${cap(((i as any).image_type as string))}: ${featureLabel(((i as any).feature as string))}`);
  const wooLabels = wooList.map(d => d.label || 'Drawing').filter(Boolean);
  const typeDisplay = pairs.length ? pairs.join(', ') : (wooLabels.length ? wooLabels.join(', ') : '-');

  return (
    <>
      <td className="p-2 border">
        {!id ? (
          '-'
        ) : isFetching ? (
          '…'
        ) : isError ? (
          <span className="text-xs text-red-600" title="Could not load images">
            Error
          </span>
        ) : has ? (
          <span className="font-bold bg-sky-100 px-2 py-1 rounded">Drawing</span>
        ) : (
          '-'
        )}
      </td>
      <td className="p-2 border">
        {!id ? '-' : isFetching ? '…' : isError ? (
          <span className="text-xs text-red-600" title="Could not load images">
            Error
          </span>
        ) : (
          typeDisplay
        )}
      </td>
      <td className="p-2 border">
        {!id ? (
          '-'
        ) : isError ? (
          <span className="text-xs text-red-600" title="Could not load images">
            Error
          </span>
        ) : has ? (
          <div className="flex gap-1">
            {hasS3 && (
              <Button size="sm" variant="secondary" onClick={() => setOpenS3(true)}>
                View
              </Button>
            )}
            {hasWoo && (
              <Button size="sm" variant="secondary" onClick={() => setOpenWoo(true)}>
                {hasS3 ? 'Website' : 'View'}
              </Button>
            )}
          </div>
        ) : (
          '-'
        )}
      </td>
      {openS3 && (
        <OrderImagesModal
          images={active.map((i) => ({
            id: (i as any).id,
            content_type: (i as any).content_type,
            image_type: (i as any).image_type,
            feature: (i as any).feature,
          }))}
          onClose={() => setOpenS3(false)}
        />
      )}
      {openWoo && <WooDrawingsModal drawings={wooList} onClose={() => setOpenWoo(false)} />}
    </>
  );
};
